import { useRef } from 'react';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useHistoryLeaveBlocker } from '@/services/navigation-guard/use-history-leave-blocker';

export type UseLeaveGuardArgs = {
  isDirty: boolean;
  message: string;
  onDiscard: () => void;
};

/**
 * Both guards read the dirty flag live rather than from render state: they
 * resolve when a navigation is released, before React has re-rendered, so a
 * render-derived flag would still say "dirty" and re-block the navigation that
 * was just allowed.
 */
export function useLeaveGuard({ isDirty, message, onDiscard }: UseLeaveGuardArgs) {
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const skipRef = useRef(false);

  const prompt = {
    hasUnsavedChanges: () => !skipRef.current && isDirtyRef.current,
    message,
    onDiscard,
  };

  // Hydra shell app-switches and tab close/refresh.
  useNavigationGuard(prompt);
  // In-app history navigations (Back/Forward), which the guard above never hears.
  useHistoryLeaveBlocker(prompt);

  return {
    allowLeave: () => {
      skipRef.current = true;
    },
  };
}
